export type Language = "en" | "ru";

const english = {
  metaTitle: "Lumina Packages — Signed RPM repository",
  metaDescription: "Browse and download signed Lumina Linux RPM packages.",
  skipToPackages: "Skip to package explorer",
  brandHome: "Lumina Packages home",
  brandTagline: "Package network",
  primaryNavigation: "Primary navigation",
  navRepositories: "Repositories",
  navPackages: "Packages",
  navSetup: "Set up DNF",
  languageSelector: "Page language",
  selectEnglish: "Show page in English",
  selectRussian: "Show page in Russian",
  switchToLight: "Switch to light theme",
  switchToDark: "Switch to dark theme",
  repositoryOnline: "Public repository online",
  heroLineOne: "Packages, signed",
  heroLineTwo: "and ready.",
  heroDescription: "Production RPMs for Lumina Linux—built by LuminaCI, verified on native Fedora workers, and delivered directly over HTTPS.",
  browsePackages: "Browse packages",
  downloadRepoFile: "Download repo file",
  repositoryGuarantees: "Repository guarantees",
  signedRpms: "GPG-signed RPMs",
  nativeBuilds: "Native architecture builds",
  atomicPublishing: "Atomic publishing",
  verified: "Verified",
  signatureGate: "signature gate",
  immutable: "Immutable",
  artifactDelivery: "artifact delivery",
  repositoryStatistics: "Repository statistics",
  publishedRpms: "Published RPMs",
  architectures: "Architectures",
  packagePayload: "Package payload",
  releaseSigning: "Release signing",
  packageNetwork: "Package network",
  repositoriesTitle: "One reliable origin.",
  repositoriesDescription: "Stable paths for current Lumina releases and compatibility mirrors.",
  recommended: "Recommended",
  lumenDescription: "Signed LuminaCI releases for Jetson R39.2.1, Orange Pi 5 Ultra, Orange Pi Zero 3, and supported Lumina architectures.",
  openIndex: "Open index",
  compatibility: "Compatibility",
  coreExtraTitle: "Core & extra",
  coreExtraDescription: "Established package trees retained for existing Lumina systems and package consumers.",
  browseCore: "Browse core",
  archive: "Archive",
  releasesUpdatesTitle: "Releases & updates",
  releasesUpdatesDescription: "Versioned release trees and update channels for reproducible system provisioning.",
  browseReleases: "Browse releases",
  explorerEyebrow: "Repository explorer",
  explorerTitle: "Find the exact build you need.",
  explorerDescription: "Every link is a direct, cache-friendly RPM download from the public repository.",
  searchPackages: "Search packages",
  searchPlaceholder: "Search name, version, or architecture",
  filterArchitecture: "Filter by architecture",
  allArchitectures: "All architectures",
  sortPackages: "Sort packages",
  sortByName: "Sort by name",
  newestFirst: "Newest first",
  largestFirst: "Largest first",
  loadingTitle: "Reading repository metadata",
  loadingDescription: "Discovering published architectures and RPMs…",
  errorTitle: "Repository index unavailable",
  errorDescription: "The package list could not be loaded. Check your connection and try again.",
  retry: "Retry",
  emptyTitle: "No matching packages",
  emptyDescription: "Try another name or architecture.",
  columnPackage: "Package",
  columnVersion: "Version",
  columnArchitecture: "Architecture",
  columnSize: "Size",
  download: "Download",
  setupEyebrow: "Fedora setup",
  setupTitle: "Connect in one command.",
  setupDescription: "Install the repository definition, then use DNF normally. Package signature checks stay enabled by default.",
  repositoryFile: "Repository file",
  publicSigningKey: "Public signing key",
  terminal: "terminal",
  copy: "Copy",
  copied: "Copied",
  signingFingerprint: "Signing fingerprint",
  footerDescription: "Public package infrastructure for Lumina Linux.",
  builtBy: "Built and verified by"
} as const;

export type Copy = { [Key in keyof typeof english]: string };

const russian: Copy = {
  metaTitle: "Lumina Packages — Репозиторий подписанных RPM",
  metaDescription: "Просматривайте и скачивайте подписанные RPM-пакеты Lumina Linux.",
  skipToPackages: "Перейти к каталогу пакетов",
  brandHome: "Главная страница Lumina Packages",
  brandTagline: "Сеть пакетов",
  primaryNavigation: "Основная навигация",
  navRepositories: "Репозитории",
  navPackages: "Пакеты",
  navSetup: "Настройка DNF",
  languageSelector: "Язык страницы",
  selectEnglish: "Показать страницу на английском языке",
  selectRussian: "Показать страницу на русском языке",
  switchToLight: "Переключить на светлую тему",
  switchToDark: "Переключить на тёмную тему",
  repositoryOnline: "Публичный репозиторий доступен",
  heroLineOne: "Пакеты подписаны",
  heroLineTwo: "и готовы.",
  heroDescription: "Готовые RPM-пакеты для Lumina Linux: собраны LuminaCI, проверены на физических узлах Fedora и доступны напрямую по HTTPS.",
  browsePackages: "Открыть пакеты",
  downloadRepoFile: "Скачать файл репозитория",
  repositoryGuarantees: "Гарантии репозитория",
  signedRpms: "RPM с GPG-подписью",
  nativeBuilds: "Нативные сборки",
  atomicPublishing: "Атомарная публикация",
  verified: "Проверено",
  signatureGate: "контроль подписи",
  immutable: "Неизменяемо",
  artifactDelivery: "доставка артефактов",
  repositoryStatistics: "Статистика репозитория",
  publishedRpms: "Опубликовано RPM",
  architectures: "Архитектуры",
  packagePayload: "Объём пакетов",
  releaseSigning: "Подпись релиза",
  packageNetwork: "Сеть пакетов",
  repositoriesTitle: "Один надёжный источник.",
  repositoriesDescription: "Стабильные пути для актуальных релизов Lumina и зеркал совместимости.",
  recommended: "Рекомендуется",
  lumenDescription: "Подписанные релизы LuminaCI для Jetson R39.2.1, Orange Pi 5 Ultra, Orange Pi Zero 3 и поддерживаемых архитектур Lumina.",
  openIndex: "Открыть индекс",
  compatibility: "Совместимость",
  coreExtraTitle: "Core и extra",
  coreExtraDescription: "Проверенные деревья пакетов для существующих систем Lumina и потребителей пакетов.",
  browseCore: "Открыть core",
  archive: "Архив",
  releasesUpdatesTitle: "Релизы и обновления",
  releasesUpdatesDescription: "Версионированные деревья релизов и каналы обновлений для воспроизводимого развёртывания систем.",
  browseReleases: "Открыть релизы",
  explorerEyebrow: "Каталог репозитория",
  explorerTitle: "Найдите нужную сборку.",
  explorerDescription: "Каждая ссылка ведёт прямо на RPM в публичном репозитории и оптимизирована для кэширования.",
  searchPackages: "Поиск пакетов",
  searchPlaceholder: "Название, версия или архитектура",
  filterArchitecture: "Фильтр по архитектуре",
  allArchitectures: "Все архитектуры",
  sortPackages: "Сортировка пакетов",
  sortByName: "По названию",
  newestFirst: "Сначала новые",
  largestFirst: "Сначала крупные",
  loadingTitle: "Читаем метаданные репозитория",
  loadingDescription: "Ищем опубликованные архитектуры и RPM…",
  errorTitle: "Индекс репозитория недоступен",
  errorDescription: "Не удалось загрузить список пакетов. Проверьте подключение и повторите попытку.",
  retry: "Повторить",
  emptyTitle: "Подходящих пакетов нет",
  emptyDescription: "Попробуйте другое название или архитектуру.",
  columnPackage: "Пакет",
  columnVersion: "Версия",
  columnArchitecture: "Архитектура",
  columnSize: "Размер",
  download: "Скачать",
  setupEyebrow: "Настройка Fedora",
  setupTitle: "Подключите одной командой.",
  setupDescription: "Установите конфигурацию репозитория и используйте DNF как обычно. Проверка подписей пакетов включена по умолчанию.",
  repositoryFile: "Файл репозитория",
  publicSigningKey: "Публичный ключ подписи",
  terminal: "терминал",
  copy: "Копировать",
  copied: "Скопировано",
  signingFingerprint: "Отпечаток ключа",
  footerDescription: "Публичная инфраструктура пакетов Lumina Linux.",
  builtBy: "Собрано и проверено"
};

export const translations: Record<Language, Copy> = {
  en: english,
  ru: russian
};

export function resolveLanguage(saved: string | null, browserLanguages: readonly string[]): Language {
  if (saved === "en" || saved === "ru") return saved;
  return browserLanguages.some(language => language.toLocaleLowerCase().startsWith("ru")) ? "ru" : "en";
}

export function formatPackageCount(count: number, language: Language): string {
  if (language === "en") return `${count} ${count === 1 ? "package" : "packages"}`;

  const category = new Intl.PluralRules("ru-RU").select(count);
  const noun = category === "one" ? "пакет" : category === "few" ? "пакета" : "пакетов";
  return `${count} ${noun}`;
}
